import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../../../../api';
import CRUDs from '../../../../api/CRUDs';
import * as EmpresaAPI from '../../../../api/empresa';
import { getEmpresaScope } from '../../../../helpers/sessionEmpresa';
import { useNavigateParams } from '../../../../hooks/useNavigateParams';
import { useSubmitLock } from '../../../../hooks/useSubmitLock';
import useFlashMessage from '../../../../hooks/useFlashMessage';
import { pickSelectedId } from '../../../../utils/funcoes';
const novo = (empresaId, tipo) => ({ empresaId, ativo: true, ...(tipo ? { descricao: '' } : { titulo: '', arquivoDocumentoTipoId: null, tipoDescricao: '', dataDocumento: '', observacao: '' }) });
export function useCadastroModule({ tipo, permissoes }) {
    const recurso = tipo ? 'arquivoDocumentoTipo' : 'arquivoDocumento', rota = tipo ? '/ArquivoDocumentoTipo' : '/ArquivoDocumento';
    const location = useLocation(), uuidParam = new URLSearchParams(location.search).get('uuid');
    const uuid = uuidParam && uuidParam !== 'null' ? uuidParam : null, editando = Boolean(uuid);
    const { empresaId } = getEmpresaScope();
    const [dados, setDados] = useState(() => novo(empresaId, tipo)), [salvos, setSalvos] = useState({});
    const [empresas, setEmpresas] = useState([]), [tipos, setTipos] = useState([]);
    const [carregando, setCarregando] = useState(true), [erro, setErro] = useState(''), [sujo, setSujo] = useState(false), [buscandoTipos, setBuscandoTipos] = useState(false);
    const geracao = useRef(0), busca = useRef(0);
    const { isSubmitting, runWithLock } = useSubmitLock(), { setFlashMessage } = useFlashMessage();
    const navigate = useNavigateParams();
    const podeSalvar = permissoes.autorizado && (editando ? permissoes.editar : permissoes.incluir);
    useEffect(() => {
        const atual = ++geracao.current; busca.current += 1;
        setSalvos({}); setDados(novo(empresaId, tipo)); setTipos([]); setSujo(false); setErro(''); setBuscandoTipos(false);
        if (!permissoes.autorizado) { setCarregando(false); return; }
        setCarregando(true);
        Promise.all([EmpresaAPI.empresa(empresaId), editando ? CRUDs[recurso].consulta(uuid) : Promise.resolve(null)])
            .then(([opcoes, response]) => {
                if (atual !== geracao.current) return;
                if (!Array.isArray(opcoes)) throw new Error('Não foi possível carregar as empresas.');
                if (editando && !response?.data?.uuid) throw new Error('Cadastro não encontrado.');
                const row = response?.data || novo(empresaId, tipo);
                const form = { ...row, ativo: row.ativo === true || row.ativo === 1, tipoDescricao: row.ArquivoDocumentoTipo?.descricao || '' };
                setEmpresas(opcoes); setDados(form); setSalvos(form);
                if (row.ArquivoDocumentoTipo) setTipos([{ ...row.ArquivoDocumentoTipo, label: '' }]);
            }).catch(e => { if (atual === geracao.current) setErro(e.response?.data?.message || e.message); })
            .finally(() => { if (atual === geracao.current) setCarregando(false); });
        return () => { geracao.current += 1; busca.current += 1; };
    }, [uuid, editando, recurso, tipo, empresaId, permissoes.autorizado]);
    const consultarTipos = async (texto = dados.tipoDescricao || '') => {
        if (tipo || !dados.empresaId || !permissoes.autorizado) return;
        const atual = ++busca.current, ctx = geracao.current; setBuscandoTipos(true);
        try {
            const { data } = await api.get('/arquivoDocumentoTipo/listar', { params: { empresaId: dados.empresaId, ativo: true, start: 0, size: 50,
                filters: JSON.stringify(texto ? [{ id: 'descricao', value: texto }] : []), filtersMode: JSON.stringify({ descricao: 'contains' }), sorting: JSON.stringify([{ id: 'descricao', desc: false }]) } });
            if (atual !== busca.current || ctx !== geracao.current) return;
            if (!Array.isArray(data.data)) throw new Error('Consulta de tipos inválida.');
            const opcoes = data.data.map(t => ({ ...t, label: '' }));
            const atualSalvo = salvos.ArquivoDocumentoTipo;
            if (atualSalvo && String(salvos.empresaId) === String(dados.empresaId) && !opcoes.some(t => t.id === atualSalvo.id)) opcoes.push({ ...atualSalvo, label: '' });
            setTipos(opcoes);
        } catch(e) { if (atual === busca.current && ctx === geracao.current) setFlashMessage(e.response?.data?.message || e.message, 'error'); }
        finally { if (atual === busca.current && ctx === geracao.current) setBuscandoTipos(false); }
    };
    const alterar = ({ target: { name, value, type: inputType, checked } }) => {
        if (!podeSalvar || isSubmitting || carregando || erro) return;
        if (name === 'empresaId') {
            if (editando) return;
            busca.current += 1; setTipos([]); setBuscandoTipos(false);
            setDados(p => ({ ...p, empresaId: value, arquivoDocumentoTipoId: null, tipoDescricao: '' }));
        } else if (name === 'tipoDescricao') {
            busca.current += 1;
            const id = pickSelectedId(value, tipos), selecionado = tipos.find(t => String(t.id) === String(id));
            setDados(p => ({ ...p, tipoDescricao: selecionado?.descricao || value, arquivoDocumentoTipoId: id }));
            if (!id) consultarTipos(value); else setBuscandoTipos(false);
        } else setDados(p => ({ ...p, [name]: inputType === 'checkbox' ? checked : value }));
        setSujo(true);
    };
    const voltar = () => { const params = new URLSearchParams(location.search); params.delete('uuid'); params.delete('btnText'); params.delete('aba'); navigate(rota, params); };
    const salvar = (e, anexosPendentes = false, continuar = false) => {
        e?.preventDefault();
        if (!podeSalvar || carregando || erro) return;
        if (anexosPendentes) { setFlashMessage('Envie ou descarte os anexos pendentes antes de salvar o cabeçalho.', 'warning'); return; }
        const ctx = geracao.current;
        return runWithLock(async () => {
            try {
                if (!dados.empresaId || !(tipo ? dados.descricao : dados.titulo)?.trim()) throw new Error('Informe empresa e ' + (tipo ? 'descrição.' : 'título.'));
                if (!tipo && !dados.arquivoDocumentoTipoId) throw new Error('Selecione um tipo da lista.');
                const payload = { uuid: uuid || undefined, empresaId: Number(dados.empresaId), ativo: dados.ativo,
                    ...(tipo ? { descricao: dados.descricao.trim() } : { titulo: dados.titulo.trim(), arquivoDocumentoTipoId: Number(dados.arquivoDocumentoTipoId), dataDocumento: dados.dataDocumento || null, observacao: dados.observacao || null }) };
                const { data } = await (editando ? CRUDs[recurso].editar(payload) : CRUDs[recurso].cadastrar(payload));
                if (ctx !== geracao.current) return;
                if (data.type !== 'success' || !data.uuid) throw new Error(data.message || 'Cadastro não salvo.');
                setFlashMessage(data.message, 'success'); setSujo(false);
                if (continuar && !tipo) {
                    const params = new URLSearchParams(location.search); params.set('uuid', data.uuid); params.set('aba', 'arquivos');
                    navigate(`${rota}/CreateOrEdit`, params);
                } else voltar();
            } catch(e) { if (ctx === geracao.current) setFlashMessage(e.response?.data?.message || e.message, 'error'); }
        });
    };
    return { dados, salvos, empresas, tipos, buscandoTipos, consultarTipos, carregando, erro, sujo, editando, podeSalvar, isSubmitting, alterar, salvar, voltar };
}
